/**
 * Avatar — iniziali colorate (sfondo magenta-soft, testo primary).
 *
 * Upload foto è rinviato a un PR successivo (richiede Firebase Storage +
 * componente upload). Per ora calcoliamo le iniziali da `name` (max 2
 * lettere maiuscole). Se name è vuoto, ripieghiamo su `email`.
 */

import { clsx } from 'clsx';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  /** Nome completo: usato per calcolare le iniziali (es. "Maria Rossi" → "MR"). */
  name?: string | null;
  /** Fallback se `name` è vuoto: usa la prima lettera dell'email. */
  email?: string | null;
  size?: AvatarSize;
  className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'w-8 h-8 text-body-sm',
  md: 'w-9 h-9 text-body-md',
  lg: 'w-12 h-12 text-body-lg',
};

function computeInitials(
  name?: string | null,
  email?: string | null
): string {
  const source = (name ?? '').trim();
  if (source.length > 0) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  const e = (email ?? '').trim();
  if (e.length > 0) {
    return e[0]!.toUpperCase();
  }
  return '?';
}

export function Avatar({ name, email, size = 'md', className }: AvatarProps) {
  const initials = computeInitials(name, email);
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-flex items-center justify-center rounded-full bg-magenta-soft text-primary font-semibold select-none',
        sizeClasses[size],
        className
      )}
    >
      {initials}
    </span>
  );
}
